import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import {
  BUCKET_COLOR_VAR,
  BUCKET_LABEL,
  type EmailDetail,
  formatAbsoluteTime,
  formatBytes,
  getEmailDetail,
} from "@/lib/emails";
import { sanitizeEmailHtml } from "@/lib/sanitizeHtml";

import { ReclassifyPanel } from "./ReclassifyPanel";

interface EmailDetailDrawerProps {
  emailId: string;
  onClose: () => void;
  /** Fires after a successful override/reset so the dashboard can refetch
   *  the list (cards may need to move columns). */
  onChanged?: () => void;
}

type DetailState =
  | { kind: "loading" }
  | { kind: "ready"; email: EmailDetail }
  | { kind: "error"; message: string };

export function EmailDetailDrawer({
  emailId,
  onClose,
  onChanged,
}: EmailDetailDrawerProps) {
  const [state, setState] = useState<DetailState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    getEmailDetail(emailId)
      .then((email) => {
        if (!cancelled) setState({ kind: "ready", email });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [emailId]);

  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Email details"
      data-testid="email-detail-drawer"
      className="fixed inset-0 z-50 flex"
    >
      <button
        type="button"
        aria-label="Close email details"
        onClick={onClose}
        data-testid="drawer-backdrop"
        className="flex-1 cursor-default bg-black/60"
      />
      <div className="flex h-full w-full max-w-[640px] flex-col border-l border-op-border bg-op-base shadow-2xl md:w-[640px]">
        <DrawerHeader state={state} onClose={onClose} />
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {state.kind === "loading" ? (
            <p
              className="rounded-lg border border-dashed border-op-border px-4 py-10 text-center text-sm text-op-muted"
              data-testid="drawer-loading"
            >
              Loading email…
            </p>
          ) : null}
          {state.kind === "error" ? (
            <p
              role="alert"
              className="rounded-lg border border-op-border bg-op-elevated px-4 py-3 text-sm text-op-secondary"
              data-testid="drawer-error"
            >
              Couldn't load the email ({state.message}).
            </p>
          ) : null}
          {state.kind === "ready" ? (
            <DrawerBody
              email={state.email}
              onUpdated={(updated) => {
                setState({ kind: "ready", email: updated });
                onChanged?.();
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface DrawerHeaderProps {
  state: DetailState;
  onClose: () => void;
}

function DrawerHeader({ state, onClose }: DrawerHeaderProps) {
  const subject =
    state.kind === "ready"
      ? state.email.subject || "(no subject)"
      : "Email";
  const bucket = state.kind === "ready" ? state.email.bucket : null;

  return (
    <header className="flex items-start justify-between gap-3 border-b border-op-border px-5 py-4">
      <div className="min-w-0 flex-1">
        {bucket ? (
          <div className="mb-2 flex items-center gap-2">
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: BUCKET_COLOR_VAR[bucket] }}
            />
            <span className="text-[11px] uppercase tracking-wider text-op-secondary">
              {BUCKET_LABEL[bucket]}
            </span>
          </div>
        ) : null}
        <h2 className="break-words text-base font-medium text-op-primary">
          {subject}
        </h2>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        data-testid="drawer-close-button"
        aria-label="Close"
      >
        Close
      </Button>
    </header>
  );
}

function DrawerBody({
  email,
  onUpdated,
}: {
  email: EmailDetail;
  onUpdated: (updated: EmailDetail) => void;
}) {
  return (
    <div className="space-y-5">
      <DrawerMetadata email={email} />
      <DrawerSummary email={email} />
      <ReclassifyPanel email={email} onUpdated={onUpdated} />
      {email.attachments.length > 0 ? (
        <DrawerAttachments email={email} />
      ) : null}
      <DrawerEmailBody email={email} />
    </div>
  );
}

function DrawerMetadata({ email }: { email: EmailDetail }) {
  const fromLabel = email.from_name
    ? `${email.from_name} <${email.from_email}>`
    : email.from_email;
  const orgLabel =
    (email.client_name && email.client_name.trim()) ||
    email.sender_display_name ||
    "Unknown sender";

  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
      <Field label="From" value={fromLabel} />
      <Field label="To" value={joinRecipients(email.to)} />
      {email.cc.length > 0 ? (
        <Field label="Cc" value={joinRecipients(email.cc)} />
      ) : null}
      <Field label="When" value={formatAbsoluteTime(email.received_at)} />
      <Field label="Org" value={`${orgLabel} · ${email.priority}`} />
    </dl>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-op-muted">{label}</dt>
      <dd className="break-words text-op-secondary">{value || "—"}</dd>
    </>
  );
}

function joinRecipients(rs: EmailDetail["to"]): string {
  return rs
    .map((r) => (r.name ? `${r.name} <${r.email}>` : r.email))
    .join(", ");
}

function DrawerSummary({ email }: { email: EmailDetail }) {
  if (!email.summary_line_1 && !email.summary_line_2) return null;
  return (
    <section
      data-testid="drawer-summary"
      className="rounded-lg border border-op-border bg-op-surface px-4 py-3"
    >
      <h3 className="mb-1 text-[10px] uppercase tracking-wider text-op-muted">
        AI summary
      </h3>
      {email.summary_line_1 ? (
        <p className="text-sm text-op-primary">{email.summary_line_1}</p>
      ) : null}
      {email.summary_line_2 ? (
        <p className="text-xs text-op-secondary">{email.summary_line_2}</p>
      ) : null}
    </section>
  );
}

function DrawerAttachments({ email }: { email: EmailDetail }) {
  return (
    <section data-testid="drawer-attachments">
      <h3 className="mb-2 text-[10px] uppercase tracking-wider text-op-muted">
        Attachments ({email.attachments.length})
      </h3>
      <ul className="space-y-1">
        {email.attachments.map((a, i) => (
          <li
            key={`${a.name}-${i}`}
            className="flex items-center justify-between rounded border border-op-border bg-op-surface px-3 py-2 text-xs"
          >
            <span className="truncate text-op-primary" title={a.name}>
              {a.name || "(unnamed)"}
            </span>
            <span className="ml-3 shrink-0 font-mono text-op-muted">
              {formatBytes(a.size)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DrawerEmailBody({ email }: { email: EmailDetail }) {
  const hasHtml = !!email.body_html;
  const sanitized = hasHtml ? sanitizeEmailHtml(email.body_html) : "";

  return (
    <section data-testid="drawer-body">
      <h3 className="mb-2 text-[10px] uppercase tracking-wider text-op-muted">
        Message
      </h3>
      {hasHtml ? (
        <div
          data-testid="drawer-body-html"
          className={cn(
            "rounded-lg border border-op-border bg-op-surface px-4 py-3 text-sm text-op-primary",
            // Inline-image safety: clamp anything huge inside the drawer.
            "[&_img]:max-w-full [&_img]:h-auto",
            "[&_a]:text-op-accent [&_a]:underline-offset-2 hover:[&_a]:underline",
            "[&_pre]:overflow-x-auto [&_pre]:bg-op-base [&_pre]:p-2 [&_pre]:text-xs",
            "[&_table]:max-w-full [&_table]:text-xs",
          )}
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      ) : (
        <pre
          data-testid="drawer-body-text"
          className="whitespace-pre-wrap break-words rounded-lg border border-op-border bg-op-surface px-4 py-3 font-sans text-sm text-op-primary"
        >
          {email.body_text || "(empty message)"}
        </pre>
      )}
    </section>
  );
}
